import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import { PlainLink } from 'components/link';
import { format0 } from 'utils/number';
import type { DupReasonKind, WellCandidate } from './consolidation-utils';
import styles from './reconcile-suggestions-table.module.scss';

/** One colour per detection rule, so a reason is recognisable before it is read. */
const REASON_TAG_TYPE: Record<DupReasonKind, 'teal' | 'blue'> = {
  'name-surface': 'teal',
  'surface-bottom': 'blue',
};

/**
 * The one-line description of a record's directional survey, shown under its name —
 * the fact most worth knowing when choosing which record survives.
 * @param {WellCandidate} well The record.
 * @return {string} The summary, or an empty string when it carries no survey.
 */
const surveySummary = (well: WellCandidate): string => {
  if (!well.survey) return '';
  return (
    `${well.survey.source} · ${well.survey.stations} stations · MD ` +
    `${format0(well.survey.mdMinFt)}–${format0(well.survey.mdMaxFt)} ft`
  );
};

export interface ReconcileSuggestionsTableProps {
  /** The rows to render (may be narrowed by the search). */
  pool: WellCandidate[];
  checkedIds: ReadonlySet<string>;
  /**
   * Whether every row across the whole pool is checked — computed by the caller over the full pool,
   * not `pool`, so it stays consistent with the global check-all/uncheck-all the header fires.
   */
  allChecked: boolean;
  onToggle: (rowId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
}

/**
 * Current Suggestions: the viewed well and the records suspected of duplicating it.
 * The checkbox is the only control here — checking is what authors the structure in the
 * consolidation panel.
 * @param {ReconcileSuggestionsTableProps} props The pool, the selection and its handlers.
 * @return {JSX.Element} The table.
 */
export function ReconcileSuggestionsTable({
  pool,
  checkedIds,
  allChecked,
  onToggle,
  onToggleAll,
}: ReconcileSuggestionsTableProps) {
  return (
    <div className={styles.wrap}>
      <Table size="md" className={styles.table}>
        <TableHead>
          <TableRow>
            <TableHeader className={styles.checkCell}>
              <Checkbox
                id="reconcile-select-all"
                labelText="Include every suggested record"
                hideLabel
                checked={allChecked}
                onChange={(
                  _event: unknown,
                  { checked }: { checked: boolean }
                ) => onToggleAll(checked)}
              />
            </TableHeader>
            <TableHeader>Source</TableHeader>
            <TableHeader>Well Name</TableHeader>
            <TableHeader>UWI</TableHeader>
            <TableHeader>UWI14</TableHeader>
            <TableHeader>Has Geometry</TableHeader>
            <TableHeader>Operator</TableHeader>
            <TableHeader>Well Status</TableHeader>
            <TableHeader>Match Reason</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {pool.map((well) => {
            const survey = surveySummary(well);
            return (
              <TableRow
                key={well.rowId}
                className={well.isAnchor ? styles.anchorRow : undefined}
              >
                <TableCell className={styles.checkCell}>
                  <Checkbox
                    id={`reconcile-check-${well.rowId}`}
                    labelText={`Include ${well.wellName}`}
                    hideLabel
                    checked={checkedIds.has(well.rowId)}
                    onChange={(
                      _event: unknown,
                      { checked }: { checked: boolean }
                    ) => onToggle(well.rowId, checked)}
                  />
                </TableCell>
                <TableCell>
                  {well.isAnchor
                    ? 'Viewed Well'
                    : well.isAdded
                      ? 'Added Well'
                      : 'Catalog Well'}
                </TableCell>
                <TableCell>
                  <span className={styles.rowName}>{well.wellName}</span>
                  {survey && <span className={styles.rowSub}>{survey}</span>}
                </TableCell>
                <TableCell>
                  <PlainLink href={`/wells/${well.uwi}`}>{well.uwi}</PlainLink>
                </TableCell>
                <TableCell>{well.uwi14 ?? '—'}</TableCell>
                <TableCell>{well.hasGeometry ? 'Yes' : 'No'}</TableCell>
                <TableCell>{well.operator}</TableCell>
                <TableCell>{well.wellStatus}</TableCell>
                <TableCell>
                  {well.isAdded ? (
                    <Tag type="gray" size="sm">
                      Added by you
                    </Tag>
                  ) : well.reasons.length === 0 ? (
                    '—'
                  ) : (
                    well.reasons.map((reason) => (
                      <Tag
                        key={reason.kind}
                        type={REASON_TAG_TYPE[reason.kind]}
                        size="sm"
                        title={reason.detail}
                      >
                        {reason.label}
                      </Tag>
                    ))
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
